import React, { createContext, useContext, useState, useEffect, ReactNode } from "react"
import { authService, User } from "../services/api"

interface AuthContextType {
  user: User | null
  isAuthenticated: boolean
  isLoading: boolean
  login: (username: string, password: string) => Promise<boolean>
  logout: () => void
  updateUser: (userData: Partial<User>) => void
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

interface AuthProviderProps {
  children: ReactNode
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  // 检查用户是否已登录
  useEffect(() => {
    const checkAuth = async () => {
      const token = localStorage.getItem("token")
      const savedUser = localStorage.getItem("user")

      if (token && savedUser) {
        try {
          // 验证token有效性
          const userData = await authService.getProfile()
          // getProfile直接返回用户数据，如果成功说明token有效
          setUser(userData)
        } catch (error) {
          // 请求失败，清除本地存储
          localStorage.removeItem("token")
          localStorage.removeItem("user")
        }
      }
      setIsLoading(false)
    }

    checkAuth()
  }, [])

  const login = async (username: string, password: string): Promise<boolean> => {
    try {
      const response = await authService.login({ username, password })
      
      if (response.token) {
        const { token, user: userData } = response
        
        // 保存到本地存储
        localStorage.setItem("token", token)
        localStorage.setItem("user", JSON.stringify(userData))
        
        // 更新状态
        setUser(userData)
        return true
      }
      return false
    } catch (error) {
      return false
    }
  }

  const logout = () => {
    // 清除本地存储
    localStorage.removeItem("token")
    localStorage.removeItem("user")
    
    // 更新状态
    setUser(null)
    
    // 调用后端logout接口（可选）
    authService.logout().catch(() => {
      // 忽略错误，因为用户已经登出
    })
  }

  const updateUser = (userData: Partial<User>) => {
    if (user) {
      const updatedUser = { ...user, ...userData }
      setUser(updatedUser)
      localStorage.setItem("user", JSON.stringify(updatedUser))
    }
  }

  const value: AuthContextType = {
    user,
    isAuthenticated: !!user,
    isLoading,
    login,
    logout,
    updateUser,
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider")
  }
  return context
} 